import { useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui-ext/primitives";
import { useUpdateEngineeringPresentationSettings } from "@/lib/engineering-insights/api";
import type { EngineeringChartSpec } from "@/lib/engineering-insights/chart-specs";
import {
  SLIDE_LAYOUT_LABELS,
  arrangementsEqual,
  resolveArrangement,
  slideLayout,
  type DeckArrangement,
  type SlideLayout,
} from "@/lib/engineering-insights/presentation";
import { cn } from "@/lib/utils";

// Deck arrangement modal. Draft edits live here (slides + the "Available
// cards" palette on the left, which is the persisted "Not presented" tray)
// and hit the server as ONE canonical PUT on Save; Cancel/Esc discards
// behind a dirty guard.
//
// Each draft slide carries an explicit layout choice that acts as its slot
// capacity (full=1, side-by-side=2, stacked-left=3). Unfilled slots render
// as drop targets, but the layout itself is draft-only scaffolding: the
// persisted shape stays ordered card-id lists, and the real presentation
// derives layout from card count — so a half-filled side-by-side saves as a
// full-screen slide and never presents an empty hole.

const TRAY_ID = "tray";
const NEW_SLIDE_ID = "new-slide";

const LAYOUT_OPTIONS: SlideLayout[] = ["full", "side-by-side", "stacked-left"];
const LAYOUT_CAPACITY: Record<SlideLayout, number> = {
  full: 1,
  "side-by-side": 2,
  "stacked-left": 3,
};

interface DraftSlide {
  key: string;
  cardIds: string[];
  layout: SlideLayout;
}

type DragKind = "card" | "slide";

const activeKind = (data: Record<string, unknown> | undefined): DragKind | null =>
  data?.type === "card" ? "card" : data?.type === "slide" ? "slide" : null;

/** Grid classes mirroring the real presentation layouts, per slot index. */
const slotClass = (layout: SlideLayout, index: number): string => {
  if (layout !== "stacked-left") {
    return "";
  }
  return [
    "col-start-1 row-start-1",
    "col-start-1 row-start-2",
    "col-start-2 row-start-1 row-span-2",
  ][index] ?? "";
};

const slideGridClass = (layout: SlideLayout): string =>
  cn(
    "grid gap-1.5",
    layout === "full" && "h-20 grid-cols-1 grid-rows-1",
    layout === "side-by-side" && "h-20 grid-cols-2 grid-rows-1",
    layout === "stacked-left" && "h-28 grid-cols-2 grid-rows-2",
  );

/** Tiny schematic of a layout for the picker buttons. */
function LayoutGlyph({ layout }: { layout: SlideLayout }) {
  const box = "rounded-[1.5px] border border-current";
  if (layout === "full") {
    return <span className={cn("block h-3 w-4", box)} />;
  }
  if (layout === "side-by-side") {
    return (
      <span className="flex h-3 w-4 gap-[1.5px]">
        <span className={cn("flex-1", box)} />
        <span className={cn("flex-1", box)} />
      </span>
    );
  }
  return (
    <span className="flex h-3 w-4 gap-[1.5px]">
      <span className="flex flex-1 flex-col gap-[1.5px]">
        <span className={cn("flex-1", box)} />
        <span className={cn("flex-1", box)} />
      </span>
      <span className={cn("flex-1", box)} />
    </span>
  );
}

function CardChip({
  spec,
  container,
  className,
  onRemove,
}: {
  spec: EngineeringChartSpec;
  container: string;
  className?: string;
  onRemove?: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: spec.id, data: { type: "card", container } });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      className={cn(
        "group relative flex min-w-0 cursor-grab touch-none flex-col justify-center rounded-[8px] border border-soft bg-surface px-2 py-1.5 shadow-card ring-focus",
        isDragging && "opacity-40",
        className,
      )}
    >
      <div className={cn("truncate text-xs font-medium text-anchor", onRemove && "pr-4")}>
        {spec.title}
      </div>
      <div className="truncate text-[10px] text-muted-foreground">{spec.section}</div>
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${spec.title} from this slide`}
          title="Remove from slide"
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={onRemove}
          className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-[4px] text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-anchor focus-visible:opacity-100 group-hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function SlideTile({
  slide,
  index,
  specById,
  onSetLayout,
  onRemoveCard,
}: {
  slide: DraftSlide;
  index: number;
  specById: Map<string, EngineeringChartSpec>;
  onSetLayout: (layout: SlideLayout) => void;
  onRemoveCard: (cardId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.key, data: { type: "slide" } });
  const capacity = LAYOUT_CAPACITY[slide.layout];
  const emptySlots = Math.max(capacity - slide.cardIds.length, 0);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        "rounded-[12px] border border-soft bg-muted/30 p-2.5",
        isDragging && "opacity-40",
      )}
    >
      <div className="mb-2 flex items-center gap-1.5">
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          aria-label={`Reorder slide ${index + 1}`}
          className="grid h-6 w-6 shrink-0 cursor-grab touch-none place-items-center rounded-md text-muted-foreground ring-focus hover:bg-muted"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <span className="text-xs font-semibold text-anchor">Slide {index + 1}</span>
        {slide.cardIds.length === 0 && (
          <span className="text-[10px] text-muted-foreground">
            empty — removed on save
          </span>
        )}
        <div className="ml-auto flex items-center gap-1" role="group" aria-label="Slide layout">
          {LAYOUT_OPTIONS.map((layout) => (
            <button
              key={layout}
              type="button"
              aria-label={SLIDE_LAYOUT_LABELS[layout]}
              aria-pressed={slide.layout === layout}
              title={SLIDE_LAYOUT_LABELS[layout]}
              onClick={() => onSetLayout(layout)}
              className={cn(
                "grid h-6 w-7 place-items-center rounded-md border transition-colors ring-focus",
                slide.layout === layout
                  ? "border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]"
                  : "border-soft text-muted-foreground hover:bg-muted hover:text-anchor",
              )}
            >
              <LayoutGlyph layout={layout} />
            </button>
          ))}
        </div>
      </div>
      <SortableContext items={slide.cardIds} strategy={rectSortingStrategy}>
        <div className={slideGridClass(slide.layout)}>
          {slide.cardIds.map((id, cardIndex) => {
            const spec = specById.get(id);
            return spec ? (
              <CardChip
                key={id}
                spec={spec}
                container={slide.key}
                className={slotClass(slide.layout, cardIndex)}
                onRemove={() => onRemoveCard(id)}
              />
            ) : null;
          })}
          {Array.from({ length: emptySlots }, (_, slot) => (
            <div
              key={`empty-${slot}`}
              className={cn(
                "grid place-items-center rounded-[8px] border border-dashed border-soft text-[10px] uppercase tracking-wider text-muted-foreground",
                slotClass(slide.layout, slide.cardIds.length + slot),
              )}
            >
              Empty slot
            </div>
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

function NewSlideZone() {
  const { setNodeRef, isOver } = useDroppable({
    id: NEW_SLIDE_ID,
    data: { type: "new-slide" },
  });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "grid h-12 place-items-center rounded-[12px] border border-dashed text-xs text-muted-foreground transition-colors",
        isOver ? "border-[var(--primary)] bg-[var(--primary)]/5 text-anchor" : "border-soft",
      )}
    >
      Drop a card here to create a new slide
    </div>
  );
}

function AvailablePanel({
  ids,
  specById,
}: {
  ids: string[];
  specById: Map<string, EngineeringChartSpec>;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: TRAY_ID,
    data: { type: "tray" },
  });
  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        className={cn(
          "flex min-h-full flex-col gap-1.5 rounded-[12px] border border-dashed p-2 transition-colors",
          isOver ? "border-[var(--primary)] bg-[var(--primary)]/5" : "border-soft",
        )}
      >
        {ids.map((id) => {
          const spec = specById.get(id);
          return spec ? <CardChip key={id} spec={spec} container={TRAY_ID} /> : null;
        })}
        {ids.length === 0 && (
          <span className="px-1 py-2 text-xs text-muted-foreground">
            Every card is on a slide. Drag one here (or remove it from its
            slide) to leave it out of the presentation.
          </span>
        )}
      </div>
    </SortableContext>
  );
}

export function ArrangeDeckModal({
  specs,
  initial,
  updatedAt,
  updatedBy,
  onClose,
}: {
  specs: readonly EngineeringChartSpec[];
  initial: DeckArrangement;
  updatedAt: string | null;
  updatedBy: string | null;
  onClose: () => void;
}) {
  const updateSettings = useUpdateEngineeringPresentationSettings();
  const specById = useMemo(() => new Map(specs.map((spec) => [spec.id, spec])), [specs]);
  const registryIndex = useMemo(
    () => new Map(specs.map((spec, index) => [spec.id, index])),
    [specs],
  );

  const slideKeyCounter = useRef(0);
  const nextSlideKey = () => `slide-${slideKeyCounter.current++}`;
  const [slides, setSlides] = useState<DraftSlide[]>(() =>
    initial.slides.map((cardIds) => ({
      key: nextSlideKey(),
      cardIds: [...cardIds],
      layout: slideLayout(cardIds.length),
    })),
  );
  const [tray, setTray] = useState<string[]>(() => [...initial.excludedIds]);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [activeSlideKey, setActiveSlideKey] = useState<string | null>(null);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const dragSnapshot = useRef<{ slides: DraftSlide[]; tray: string[] } | null>(null);

  // The palette is a set; it always displays (and saves) in registry order.
  const trayIds = useMemo(
    () =>
      [...tray].sort(
        (a, b) => (registryIndex.get(a) ?? 0) - (registryIndex.get(b) ?? 0),
      ),
    [tray, registryIndex],
  );

  const draftArrangement = useMemo(
    () =>
      resolveArrangement(specs, {
        slides: slides.map((slide) => slide.cardIds),
        excludedIds: trayIds,
      }),
    [specs, slides, trayIds],
  );
  const initialArrangement = useMemo(
    () => resolveArrangement(specs, initial),
    [specs, initial],
  );
  const dirty = !arrangementsEqual(draftArrangement, initialArrangement);
  const placedCount = draftArrangement.slides.reduce(
    (total, slide) => total + slide.length,
    0,
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Slides only collide with slides; cards with everything else. Without the
  // filter, a dragged slide could "enter" its own card list. Pointer drags
  // resolve by pointer position first so thin targets (the new-slide zone)
  // beat the large palette beneath them; keyboard drags have no pointer and
  // fall through to closestCorners.
  const collisionDetection: CollisionDetection = (args) => {
    const kind = activeKind(args.active.data.current);
    const droppableContainers = args.droppableContainers.filter((container) => {
      const isSlide = container.data.current?.type === "slide";
      return kind === "slide" ? isSlide : true;
    });
    const withPointer = pointerWithin({ ...args, droppableContainers });
    if (withPointer.length > 0) {
      return withPointer;
    }
    return closestCorners({ ...args, droppableContainers });
  };

  const findContainer = (cardId: string): string | null => {
    const slide = slides.find((entry) => entry.cardIds.includes(cardId));
    if (slide) {
      return slide.key;
    }
    return tray.includes(cardId) ? TRAY_ID : null;
  };

  /** Resolve a droppable/sortable target to the container it stands for. */
  const containerOf = (id: UniqueIdentifier, data: Record<string, unknown> | undefined): string | null => {
    if (data?.type === "card") {
      return (data.container as string) ?? null;
    }
    if (data?.type === "slide") {
      return String(id);
    }
    if (data?.type === "tray" || id === TRAY_ID) {
      return TRAY_ID;
    }
    return null;
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    dragSnapshot.current = {
      slides: slides.map((slide) => ({ ...slide, cardIds: [...slide.cardIds] })),
      tray: [...tray],
    };
    const kind = activeKind(active.data.current);
    if (kind === "card") {
      setActiveCardId(String(active.id));
    } else if (kind === "slide") {
      setActiveSlideKey(String(active.id));
    }
  };

  const moveCard = (cardId: string, target: string, overCardId: string | null) => {
    const source = findContainer(cardId);
    if (source === null || source === target) {
      return;
    }
    if (target !== TRAY_ID) {
      const targetSlide = slides.find((entry) => entry.key === target);
      if (
        !targetSlide ||
        targetSlide.cardIds.length >= LAYOUT_CAPACITY[targetSlide.layout]
      ) {
        return;
      }
    }
    if (source === TRAY_ID) {
      setTray((current) => current.filter((id) => id !== cardId));
    } else {
      setSlides((current) =>
        current.map((slide) =>
          slide.key === source
            ? { ...slide, cardIds: slide.cardIds.filter((id) => id !== cardId) }
            : slide,
        ),
      );
    }
    if (target === TRAY_ID) {
      setTray((current) => (current.includes(cardId) ? current : [...current, cardId]));
    } else {
      setSlides((current) =>
        current.map((slide) => {
          if (slide.key !== target) {
            return slide;
          }
          const cardIds = [...slide.cardIds];
          const overIndex = overCardId ? cardIds.indexOf(overCardId) : -1;
          cardIds.splice(overIndex === -1 ? cardIds.length : overIndex, 0, cardId);
          return { ...slide, cardIds };
        }),
      );
    }
  };

  // Cross-container moves happen live during dragOver (the standard dnd-kit
  // multi-container pattern) so sortable previews stay coherent; the
  // new-slide zone is deliberately dragEnd-only to avoid containers
  // appearing mid-drag under the pointer.
  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (activeKind(active.data.current) !== "card" || !over) {
      return;
    }
    if (over.id === NEW_SLIDE_ID) {
      return;
    }
    const target = containerOf(over.id, over.data.current);
    if (target) {
      moveCard(
        String(active.id),
        target,
        over.data.current?.type === "card" ? String(over.id) : null,
      );
    }
  };

  // Slides emptied BY THIS DRAG prune here; slides that were already empty
  // before it started (chip-X or layout-trim scaffolds) stay to be refilled,
  // and canonicalization drops any still-empty ones on save.
  const finishDrag = () => {
    const before = dragSnapshot.current;
    setSlides((current) =>
      current.filter((slide) => {
        if (slide.cardIds.length > 0) {
          return true;
        }
        const preDrag = before?.slides.find((entry) => entry.key === slide.key);
        return !preDrag || preDrag.cardIds.length === 0;
      }),
    );
    setActiveCardId(null);
    setActiveSlideKey(null);
    dragSnapshot.current = null;
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const kind = activeKind(active.data.current);
    if (kind === "slide" && over && over.id !== active.id) {
      setSlides((current) => {
        const from = current.findIndex((slide) => slide.key === active.id);
        const to = current.findIndex((slide) => slide.key === over.id);
        return from === -1 || to === -1 ? current : arrayMove(current, from, to);
      });
    }
    if (kind === "card" && over) {
      const cardId = String(active.id);
      if (over.id === NEW_SLIDE_ID) {
        const source = findContainer(cardId);
        if (source === TRAY_ID) {
          setTray((current) => current.filter((id) => id !== cardId));
        } else if (source) {
          setSlides((current) =>
            current.map((slide) =>
              slide.key === source
                ? { ...slide, cardIds: slide.cardIds.filter((id) => id !== cardId) }
                : slide,
            ),
          );
        }
        setSlides((current) => [
          ...current,
          { key: nextSlideKey(), cardIds: [cardId], layout: "full" },
        ]);
      } else if (over.data.current?.type === "card") {
        // Same-container reorder (cross-container already applied in dragOver).
        const container = findContainer(cardId);
        if (container && container !== TRAY_ID && container === containerOf(over.id, over.data.current)) {
          setSlides((current) =>
            current.map((slide) => {
              if (slide.key !== container) {
                return slide;
              }
              const from = slide.cardIds.indexOf(cardId);
              const to = slide.cardIds.indexOf(String(over.id));
              return from === -1 || to === -1
                ? slide
                : { ...slide, cardIds: arrayMove(slide.cardIds, from, to) };
            }),
          );
        }
      }
    }
    finishDrag();
  };

  const handleDragCancel = () => {
    if (dragSnapshot.current) {
      setSlides(dragSnapshot.current.slides);
      setTray(dragSnapshot.current.tray);
    }
    setActiveCardId(null);
    setActiveSlideKey(null);
    dragSnapshot.current = null;
  };

  const setSlideLayout = (key: string, layout: SlideLayout) => {
    const slide = slides.find((entry) => entry.key === key);
    if (!slide || slide.layout === layout) {
      return;
    }
    const capacity = LAYOUT_CAPACITY[layout];
    const overflow = slide.cardIds.slice(capacity);
    setSlides((current) =>
      current.map((entry) =>
        entry.key === key
          ? { ...entry, layout, cardIds: entry.cardIds.slice(0, capacity) }
          : entry,
      ),
    );
    if (overflow.length > 0) {
      setTray((current) => [
        ...current,
        ...overflow.filter((id) => !current.includes(id)),
      ]);
    }
  };

  const removeCardFromSlide = (key: string, cardId: string) => {
    setSlides((current) =>
      current.map((entry) =>
        entry.key === key
          ? { ...entry, cardIds: entry.cardIds.filter((id) => id !== cardId) }
          : entry,
      ),
    );
    setTray((current) => (current.includes(cardId) ? current : [...current, cardId]));
  };

  const requestClose = () => {
    if (updateSettings.isPending) {
      return;
    }
    if (dirty) {
      setConfirmingDiscard(true);
    } else {
      onClose();
    }
  };

  const save = () => {
    updateSettings.mutate(draftArrangement, { onSuccess: onClose });
  };

  const activeSpec = activeCardId ? specById.get(activeCardId) : null;
  const activeSlide = activeSlideKey
    ? slides.find((slide) => slide.key === activeSlideKey)
    : null;

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) {
          requestClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content
          onEscapeKeyDown={(event) => {
            // Esc first cancels an in-flight drag (dnd-kit handles it).
            if (activeCardId || activeSlideKey) {
              event.preventDefault();
            }
          }}
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[min(1120px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[16px] border border-soft bg-surface shadow-card"
        >
          <div className="flex items-start justify-between gap-3 border-b border-soft px-5 py-4">
            <div>
              <Dialog.Title className="text-base font-semibold text-anchor">
                Arrange presentation
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-muted-foreground">
                Drag cards from the left panel onto slides, pick each slide's layout,
                and reorder the deck. Cards left in the panel stay out of the
                presentation; unfilled slots collapse when you save.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] border border-soft text-anchor transition-colors ring-focus hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="flex min-h-0 flex-1">
              <aside className="flex w-64 shrink-0 flex-col border-r border-soft">
                <div className="px-4 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Available cards · {trayIds.length}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                  <AvailablePanel ids={trayIds} specById={specById} />
                </div>
              </aside>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Deck · {draftArrangement.slides.length}{" "}
                  {draftArrangement.slides.length === 1 ? "slide" : "slides"} ·{" "}
                  {placedCount} of {specs.length} cards
                </div>
                <SortableContext
                  items={slides.map((slide) => slide.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-2.5">
                    {slides.map((slide, index) => (
                      <SlideTile
                        key={slide.key}
                        slide={slide}
                        index={index}
                        specById={specById}
                        onSetLayout={(layout) => setSlideLayout(slide.key, layout)}
                        onRemoveCard={(cardId) => removeCardFromSlide(slide.key, cardId)}
                      />
                    ))}
                    <NewSlideZone />
                  </div>
                </SortableContext>
              </div>
            </div>

            <DragOverlay>
              {activeSpec ? (
                <div className="w-44 rounded-[8px] border border-soft bg-surface px-2 py-1.5 shadow-card">
                  <div className="truncate text-xs font-medium text-anchor">
                    {activeSpec.title}
                  </div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    {activeSpec.section}
                  </div>
                </div>
              ) : activeSlide ? (
                <div className="rounded-[12px] border border-soft bg-surface px-3 py-2 shadow-card text-xs font-semibold text-anchor">
                  Slide · {activeSlide.cardIds.length}{" "}
                  {activeSlide.cardIds.length === 1 ? "card" : "cards"}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          <div className="border-t border-soft px-5 py-3.5">
            {updateSettings.isError && (
              <div className="mb-3 rounded-[10px] border border-[var(--danger)]/20 bg-[var(--danger)]/5 px-3 py-2 text-xs text-[var(--danger)]">
                Failed to save the arrangement —{" "}
                {updateSettings.error instanceof Error
                  ? updateSettings.error.message
                  : "please try again."}
              </div>
            )}
            {confirmingDiscard ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-anchor">Discard your changes to the deck?</span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmingDiscard(false)}
                  >
                    Keep editing
                  </Button>
                  <Button variant="danger" size="sm" onClick={onClose}>
                    Discard
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-muted-foreground">
                  {updatedAt
                    ? `Last arranged by ${updatedBy ?? "unknown"} · ${new Date(
                        updatedAt,
                      ).toLocaleString()}`
                    : "Not arranged yet — showing the default order."}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={updateSettings.isPending}
                    onClick={requestClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={updateSettings.isPending || !dirty}
                    onClick={save}
                  >
                    {updateSettings.isPending && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    )}
                    Save arrangement
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
